import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { isFeestdag, feestdagNaam } from '../utils/feestdagen';
import WerkorderModal from '../pages/WerkorderModal';

const MND = ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'];
const ds = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const SOORT_ICOON = { 'Ziek': '🤒', 'Verlof': '🏖️', 'Cursus': '📚' };

export default function DrukteKalender({ defaultUurPerDag = 6.4 }) {
  const [mode, setMode] = useState('maand'); // maand | kwartaal | jaar
  const [anker, setAnker] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [uurPerDag, setUurPerDag] = useState(defaultUurPerDag);
  const [afdelingId, setAfdelingId] = useState('');
  const [data, setData] = useState({ dagen: {}, monteurs: 0, afdelingen: [], mode: 'eigen' });
  const [sel, setSel] = useState(null);
  const [selData, setSelData] = useState(null);
  const [showPlan, setShowPlan] = useState(false);
  const navigate = useNavigate();

  const periode = useMemo(() => {
    const y = anker.getFullYear(), m = anker.getMonth();
    if (mode === 'maand')    return { jaar: y, maanden: [m] };
    if (mode === 'kwartaal') { const q = Math.floor(m/3)*3; return { jaar: y, maanden: [q, q+1, q+2] }; }
    return { jaar: y, maanden: [0,1,2,3,4,5,6,7,8,9,10,11] };
  }, [mode, anker]);

  const startStr = ds(new Date(periode.jaar, periode.maanden[0], 1));
  const eindStr  = ds(new Date(periode.jaar, periode.maanden[periode.maanden.length-1] + 1, 0));

  const laadDagen = () => {
    const params = { start: startStr, eind: eindStr };
    if (afdelingId) params.afdeling_id = afdelingId;
    api.get('/forecast/drukte', { params })
      .then(r => setData(r.data))
      .catch(() => setData({ dagen: {}, monteurs: 0, afdelingen: [], mode: 'eigen' }));
  };

  useEffect(() => {
    setSel(null); setSelData(null);
    laadDagen();
  }, [startStr, eindStr, afdelingId]);

  const stap = (r) => {
    const y = anker.getFullYear(), m = anker.getMonth();
    if (mode === 'maand') setAnker(new Date(y, m + r, 1));
    else if (mode === 'kwartaal') setAnker(new Date(y, m + 3*r, 1));
    else setAnker(new Date(y + r, m, 1));
  };

  const nu = new Date();
  const vandaagStr = ds(nu);
  const huidigeMaandStart = new Date(nu.getFullYear(), nu.getMonth(), 1);

  const dagInfo = (d) => {
    const fd = isFeestdag(d);
    const info = data.dagen[ds(d)] || { werkorders: 0, gepland_uren: 0, categorie_uren: 0, afwezig: 0 };
    const beschikbareMonteurs = Math.max(data.monteurs - (info.rooster_vrij || 0), 0);
    const cap = fd ? 0 : Math.max(beschikbareMonteurs * uurPerDag - (info.categorie_uren || 0), 0);
    const verwacht = info.verwacht_uren || 0;
    const vrij = cap - (info.gepland_uren || 0) - verwacht;
    const belast = (info.gepland_uren || 0) + verwacht;
    const pct = cap > 0 ? (belast / cap) * 100 : (belast > 0 ? 999 : 0);
    let bg = '#dcfce7', fg = '#166534';
    if (fd)             { bg = '#ffedd5'; fg = '#9a3412'; }
    else if (pct > 100) { bg = '#fca5a5'; fg = '#7f1d1d'; }
    else if (pct > 85)  { bg = '#fecaca'; fg = '#b91c1c'; }
    else if (pct > 50)  { bg = '#fef3c7'; fg = '#92400e'; }
    return { info, cap, vrij, pct, fd, bg, fg, verwacht };
  };

  const klikDag = async (d) => {
    const k = ds(d);
    setSel(k);
    try {
      const params = { datum: k };
      if (afdelingId) params.afdeling_id = afdelingId;
      const r = await api.get('/forecast/drukte/dag', { params });
      setSelData(r.data);
    } catch { setSelData(null); }
  };

  const renderMaand = (m, compact) => {
    const y = periode.jaar;
    const maandVerleden = new Date(y, m + 1, 0) < huidigeMaandStart;
    const laatsteDag = new Date(y, m + 1, 0).getDate();
    const cells = [];
    let eerste = new Date(y, m, 1);
    while (eerste.getDay() === 0 || eerste.getDay() === 6) eerste.setDate(eerste.getDate() + 1);
    for (let i = 0; i < eerste.getDay() - 1; i++) cells.push(<div key={'pad' + i} />);

    for (let dag = 1; dag <= laatsteDag; dag++) {
      const d = new Date(y, m, dag);
      if (d.getDay() === 0 || d.getDay() === 6) continue;
      const di = dagInfo(d);
      const k = ds(d);
      const bg = maandVerleden ? '#f3f4f6' : di.bg;
      const fg = maandVerleden ? '#9ca3af' : di.fg;
      const titel = di.fd
        ? feestdagNaam(d)
        : `${di.info.werkorders} wo · ${di.info.gepland_uren.toFixed(1)}u gepland · ${Math.max(di.vrij,0).toFixed(1)}u vrij${di.verwacht ? ` · 🔮 ${di.verwacht.toFixed(1)}u verwacht onderhoud (${di.info.verwacht_beurten})` : ''}${di.info.afwezig ? ` · ${di.info.afwezig} afwezig` : ''}`;
      cells.push(
        <div key={k} onClick={() => !compact && klikDag(d)} title={titel}
          style={{
            background: bg, color: fg, borderRadius: compact ? 3 : 8,
            padding: compact ? 0 : '6px 4px', minHeight: compact ? 14 : 58,
            cursor: compact ? 'default' : 'pointer', fontSize: compact ? 0 : 11,
            outline: sel === k ? '2px solid #2563eb' : 'none', textAlign: 'center'
          }}>
          {!compact && (
            <>
              <div style={{ fontWeight: 700 }}>{dag}</div>
              {di.fd
                ? <div style={{ fontSize: 9 }}>🎉</div>
                : <>
                    <div style={{ fontWeight: 600 }}>{di.vrij >= 0 ? `+${di.vrij.toFixed(0)}u` : `${di.vrij.toFixed(0)}u`}</div>
                    <div style={{ fontSize: 9, opacity: 0.8 }}>{di.info.werkorders} wo{di.verwacht ? ' · 🔮' : ''}{di.info.afwezig ? ` · 🏖️${di.info.afwezig}` : ''}</div>
                  </>}
            </>
          )}
        </div>
      );
    }

    return (
      <div key={m} style={{ flex: compact ? '1 1 220px' : '1 1 320px', minWidth: compact ? 200 : 300, opacity: maandVerleden ? 0.7 : 1 }}>
        <div style={{ fontWeight: 700, fontSize: compact ? 12 : 14, margin: '4px 0', color: maandVerleden ? '#9ca3af' : '#111827' }}>{MND[m]} {y}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: compact ? 2 : 4 }}>
          {!compact && ['Ma','Di','Wo','Do','Vr'].map(h => <div key={h} style={{ fontSize: 10, color: '#6b7280', textAlign: 'center' }}>{h}</div>)}
          {cells}
        </div>
      </div>
    );
  };

  const naPlannen = () => {
    setShowPlan(false);
    laadDagen();
    if (sel) {
      const params = { datum: sel };
      if (afdelingId) params.afdeling_id = afdelingId;
      api.get('/forecast/drukte/dag', { params }).then(r => setSelData(r.data)).catch(() => {});
    }
  };

  const label = mode === 'maand' ? `${MND[anker.getMonth()]} ${anker.getFullYear()}`
    : mode === 'kwartaal' ? `Q${Math.floor(anker.getMonth()/3)+1} ${anker.getFullYear()}`
    : `${anker.getFullYear()}`;

  const btn = (actief) => ({
    padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer',
    background: actief ? '#2563eb' : '#fff', color: actief ? '#fff' : '#374151', fontSize: 12, fontWeight: 600
  });

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginTop: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 17 }}>📅 Bezettingskalender</h2>
        <div style={{ display: 'flex', gap: 4 }}>
          {['maand','kwartaal','jaar'].map(mo => (
            <button key={mo} style={btn(mode === mo)} onClick={() => setMode(mo)}>{mo[0].toUpperCase() + mo.slice(1)}</button>
          ))}
        </div>
        {data.mode === 'alle' && (
          <select value={afdelingId} onChange={e => setAfdelingId(e.target.value)}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }}>
            <option value="">Alle afdelingen</option>
            {(data.afdelingen || []).map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
          <button style={btn(false)} onClick={() => stap(-1)}>←</button>
          <span style={{ fontWeight: 700, minWidth: 110, textAlign: 'center', fontSize: 13 }}>{label}</span>
          <button style={btn(false)} onClick={() => stap(1)}>→</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 12, color: '#6b7280', marginBottom: 12, flexWrap: 'wrap' }}>
        <span>👷 <strong>{data.monteurs}</strong> monteurs (uit DB)</span>
        <label>Uur/dag p.m. <input type="number" value={uurPerDag} min={1} step={0.5}
          onChange={e => setUurPerDag(parseFloat(e.target.value) || 1)}
          style={{ width: 55, marginLeft: 4 }} /></label>
        <span>🟩 ruimte · 🟨 &gt;50% · 🟥 &gt;85% · donkerrood = overboekt · 🔮 verwacht onderhoud · 🎉 feestdag · 🏖️ afwezig</span>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {periode.maanden.map(m => renderMaand(m, mode === 'jaar'))}
      </div>

      {sel && selData && (
        <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ fontSize: 13 }}>{sel}</strong>
            {sel >= vandaagStr ? (
              <button onClick={() => setShowPlan(true)} style={{ ...btn(true), marginLeft: 'auto' }}>
                + Inplannen
              </button>
            ) : (
              <button disabled title="Verleden — niet inplanbaar"
                style={{ ...btn(false), opacity: 0.5, cursor: 'not-allowed', marginLeft: 'auto' }}>
                + Inplannen
              </button>
            )}
          </div>
          {sel && (data.dagen[sel]?.verwacht_uren || 0) > 0 && (
            <div style={{ fontSize: 12, color: '#7c3aed', margin: '6px 0' }}>
              🔮 Verwacht onderhoud: {data.dagen[sel].verwacht_uren.toFixed(1)}u ({data.dagen[sel].verwacht_beurten} beurt{data.dagen[sel].verwacht_beurten === 1 ? '' : 'en'}) — nog niet als werkorder aangemaakt
            </div>
          )}
          {(selData.afwezig || []).length > 0 && (
            <div style={{ fontSize: 12, color: '#0891b2', margin: '6px 0' }}>
              {selData.afwezig.map((a, i) => (
                <span key={i} style={{ marginRight: 10 }}>
                  {SOORT_ICOON[a.soort] || '📌'} {a.naam} — {a.soort} {a.uren}u
                </span>
              ))}
            </div>
          )}
          {(selData.items || []).length === 0
            ? <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Niets gepland — ruimte voor projecten 👍</div>
            : (selData.items || []).map(w => (
                <div key={w.id} onClick={() => navigate('/werkorder/' + w.id)} title="Open werkorder" style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}>
                  <strong>{w.titel}</strong>{w.ordernummer ? ` · ${w.ordernummer}` : ''} · {w.uren}u
                  {w.afdeling ? ` · ${w.afdeling}` : ''}{w.monteurs ? ` · ${w.monteurs}` : ''}
                </div>
              ))}
        </div>
      )}
      <WerkorderModal show={showPlan} onClose={() => setShowPlan(false)} onSuccess={naPlannen} defaultDatum={sel} />
    </div>
  );
}
